import { motion } from 'framer-motion';
import React from 'react';

import { cn } from './cn';
import { Loader } from './Loader';

export interface PivotItem {
  headerText: string;
  key: string;
  content?: React.ReactNode;
  loading?: boolean;
  icon?: React.ReactNode;
}

export interface PivotProps {
  items: PivotItem[];
  selectedKey?: string;
  onTabChange?: (key: string) => void;
  className?: string;
  /**
   * Stretch the Pivot and its tab content to fill the parent's remaining height (grow / min-h-0 flex
   * chain), for full-height tab panels like scrollable lists. Opt-in: it changes how the content
   * resolves percentage heights and overflows, which most tab panels don't want.
   */
  fill?: boolean;
}

export const Pivot = ({ items, selectedKey, onTabChange, className, fill }: PivotProps) => {
  const [internalKey, setInternalKey] = React.useState(items[0]?.key || '');
  const activeKey = selectedKey !== undefined ? selectedKey : internalKey;
  const activeItem = items.find((item) => item.key === activeKey);

  const handleTabClick = (key: string) => {
    if (selectedKey === undefined) setInternalKey(key);
    onTabChange?.(key);
  };

  return (
    <div className={cn('flex flex-col', fill && 'grow min-h-0', className)}>
      <div className="flex border-b border-border" role="tablist">
        {items.map((item) => {
          const isActive = item.key === activeKey;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => handleTabClick(item.key)}
              className={cn(
                'relative flex items-center gap-1.5 px-4 py-2.5 font-heading text-sm font-medium transition-colors duration-200 cursor-pointer bg-transparent border-none',
                isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
              )}
            >
              {item.icon}
              {item.headerText}
              {isActive && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"
                  layoutId="pivot-underline"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
            </button>
          );
        })}
      </div>
      <div className={cn('mt-4', fill && 'flex flex-col flex-1 min-h-0')}>
        {activeItem?.loading ? <Loader /> : activeItem?.content}
      </div>
    </div>
  );
};
